import { StateCreator } from 'zustand';
import * as L from 'leaflet';
import { enableEditing, disableEditing } from '../../MapFunctions/MapHelperFunctions';

type EntityType = 'Place' | 'Gateway' | 'Stationary';

const getFCKey = (entityType?: string) => {
  const t = entityType?.toLowerCase().split(' ')[0];
  if (t === 'place') return 'placeFC';
  if (t === 'gateway') return 'gatewayFC';
  if (t === 'stationary') return 'stationaryFC';
  return null;
};

const isSameFeature = (f: any, feature: any) =>
  f === feature || (!!feature?.properties?.Id && f?.properties?.Id === feature.properties.Id);
export interface MapObjectSlice {
  // --- Creation flags ---
  createPlace: boolean;
  setCreatePlace: (val: boolean) => void;

  createGateway: boolean;
  setCreateGateway: (val: boolean) => void;

  createStationary: boolean;
  setCreateStationary: (val: boolean) => void;

  createScale: boolean;
  setCreateScale: (val: boolean) => void;

  // --- Scale data ---
  scaleLength: string;
  setScaleLength: (val: string) => void;

  // --- Object metadata ---
  newObjId: string;
  setNewObjId: (val: string) => void;

  newObjName: string;
  setNewObjName: (val: string) => void;

  newObjKeywords: any[];
  setNewObjKeywords: (val: any[]) => void;

  newObjFloor: string;
  setNewObjFloor: (val: string) => void;

  newObjOnline: boolean | null;
  setNewObjOnline: (val: boolean | null) => void;

  newObjCreated: any;
  setNewObjCreated: (val: any) => void;

  newScaleCreated: any;
  setNewScaleCreated: (val: any) => void;

  // --- Control flags ---
  isCreating: boolean;
  setIsCreating: (val: boolean) => void;

  addScale: boolean;
  setAddScale: (val: boolean) => void;

  editItem: L.Layer | null;
  editRevision: number;
  setEditItem: (layer: L.Layer | null) => void;

  // --- Actions ---
  addObject: (entityType: string) => void;
  updateObject: (event: L.Layer) => void;
  deleteObject: () => void;
  addTempObject: (event: L.Layer) => void;
  removeTempObject: () => void;
}

// --- Initial state for easy reset ---
export const mapObjectInitialState = {
  createPlace: false,
  createGateway: false,
  createStationary: false,
  createScale: false,

  scaleLength: '',

  newObjId: '',
  newObjName: '',
  newObjKeywords: [],
  newObjFloor: '',
  newObjOnline: null,
  newObjCreated: null,
  newScaleCreated: null,
  editItem: null,
  editRevision: 0,
  isCreating: false, // UPDATED

  addToMap: false,
  addScale: false,
  deleteItem: false,
  updateItem: false,
  removeTempItem: false,
};

export const createMapObjectSlice: StateCreator<MapObjectSlice> = (set, get) => ({
  ...mapObjectInitialState,

  // --- Setters ---
  setCreatePlace: (val) => set({ createPlace: val }),
  setCreateGateway: (val) => set({ createGateway: val }),
  setCreateStationary: (val) => set({ createStationary: val }),
  setCreateScale: (val) => set({ createScale: val }),

  setScaleLength: (val) => set({ scaleLength: val }),

  setNewObjId: (val) => set({ newObjId: val }),
  setNewObjName: (val) => set({ newObjName: val }),
  setNewObjKeywords: (val) => set({ newObjKeywords: val }),
  setNewObjFloor: (val) => set({ newObjFloor: val }),
  setNewObjOnline: (val) => set({ newObjOnline: val }),
  setNewObjCreated: (val) => set({ newObjCreated: val }),
  setNewScaleCreated: (val) => set({ newScaleCreated: val }),
  setAddScale: (val) => set({ addScale: val }),
  setIsCreating: (val) => set({ isCreating: val }), // UPDATED

  // --- Actions ---
  addObject: (entityType) => {
    const { newObjId, newObjName, newObjKeywords, newObjFloor, drawEvent, tempLayer } =
      get() as any;
    if (!drawEvent) return;

    const layer = (drawEvent as any).layer;
    if (!layer) return;

    const fcKey = getFCKey(entityType);
    if (!fcKey) return;

    const feature = {
      type: 'Feature',
      // Current position, including any drag before confirming
      geometry: layer.toGeoJSON().geometry,
      properties: {
        // Keeps OnlineStatus and anything else addTempObject put on the preview
        ...layer.feature?.properties,
        Id: newObjId,
        Name: newObjName,
        Keywords: newObjKeywords,
        Floor: newObjFloor,
        EntityType: entityType,
      },
    };

    drawEvent.feature = feature;

    tempLayer?.removeLayer(layer);

    set((state: any) => {
      const fc = state[fcKey] ?? { type: 'FeatureCollection', features: [] };
      return { [fcKey]: { ...fc, features: [...fc.features, feature] } };
    });
  },

  updateObject: (event) => {
    try {
      const layer = event as any;
      const feature = layer.feature;
      const fcKey = getFCKey(feature?.properties?.EntityType);
      if (!fcKey) return;
      const fc = (get() as any)[fcKey];
      const target = fc?.features?.find((f: any) => isSameFeature(f, feature));
      if (!target) return;

      target.geometry = layer.toGeoJSON().geometry;
    } catch (err) {
      console.warn('Update object skipped:', err);
    }
  },

  deleteObject: () => {
    try {
      const { setClickEvent, clickEvent } = get() as any;
      const feature = (clickEvent as any)?.feature;
      const fcKey = getFCKey(feature?.properties?.EntityType);

      if (fcKey) {
        set((state: any) => {
          const fc = state[fcKey];
          if (!fc) return {};
          return {
            [fcKey]: {
              ...fc,
              features: fc.features.filter((f: any) => !isSameFeature(f, feature)),
            },
          };
        });
      }

      setClickEvent(null);
    } catch (err) {
      console.warn('Delete object skipped:', err);
    }
  },

  setEditItem: (layer) =>
    set((state) => {
      if (state.editItem && state.editItem !== layer) {
        disableEditing(state.editItem);
      }

      if (layer) {
        disableEditing(layer);
        enableEditing(layer);

        return {
          editItem: layer,
          editRevision: state.editRevision + 1,
        };
      }

      return { editItem: null, editRevision: state.editRevision + 1 };
    }),

  addTempObject: (event) => {
    if (!event) return;

    const { newObjId, newObjName, newObjKeywords, newObjFloor, newObjOnline, tempLayer } =
      get() as any;

    const layer = (event as any).layer;
    if (!layer) return;

    let feature = (layer as any).feature ?? ((layer as any).feature = {});
    feature.type = 'Feature';
    feature.properties = {
      ...feature.properties,
      Id: newObjId,
      Name: newObjName,
      Keywords: newObjKeywords,
      Floor: newObjFloor,
      OnlineStatus: newObjOnline,
    };

    tempLayer?.addLayer(layer);
    set({ newObjCreated: layer });
  },

  removeTempObject: () => {
    const { drawEvent, tempLayer } = get() as any;
    try {
      if (!drawEvent) return;
      const layer = drawEvent.layer;
      if (!layer) return;
      tempLayer?.removeLayer(layer);
    } catch (err) {
      console.warn('Temp object removal skipped:', err);
    } finally {
      set((state: any) => ({
        newObjId: '',
        newObjName: '',
        newObjKeywords: [],
        newObjFloor: '',
        newObjOnline: null,
        newObjCreated: null,
        addToMap: false,
        removeTempItem: false,
        drawEvent: null,
      }));
    }
  },
});
